// Shared by the Sites, Clients and Contacts list toolbars. Short option lists get
// a plain Select; long ones (entities, roles) get a searchable Command popover,
// because scrolling a 900-entity Select to find one name isn't filtering.

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../../foundation/altzero_base/base/components/ui/command';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';

// Plurals for the "All <label>" placeholder — naive `${label}s` yields
// "All Citys" / "All Statuss". Only the irregular ones need an entry.
const PLURAL: Record<string, string> = {
  City: 'Cities',
  Status: 'Statuses',
  'Parse status': 'Parse statuses',
  Entity: 'Entities',
};

const allLabel = (label: string) => `All ${PLURAL[label] ?? `${label}s`}`;

type FilterDropdownProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  /** Force the searchable popover regardless of how many options there are. */
  searchable?: boolean;
  /** The "no filter" option's text, where "All <label>s" reads wrong ("Any month"). */
  allText?: string;
  testId?: string;
};

export function FilterDropdown({ label, value, onChange, options, searchable, allText, testId }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const all = allText ?? allLabel(label);
  // Radix Select forbids empty-string option values (it reserves '' to clear
  // the selection). Drop any empties that slip through from the RPC.
  const opts = options.filter((o) => o !== '');

  if (!searchable && opts.length <= 10) {
    return (
      <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
        <SelectTrigger aria-label={label} data-testid={testId} className="h-9 w-40 text-xs">
          <SelectValue placeholder={all} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{all}</SelectItem>
          {opts.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={label}
          data-testid={testId}
          className={cn('h-9 w-44 justify-between text-xs font-normal', value && 'border-blue-400 text-blue-700')}
        >
          <span className="truncate">{value || all}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label}…`} className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => { onChange(''); setOpen(false); }}>
                <Check className={cn('mr-2 h-3.5 w-3.5', value === '' ? 'opacity-100' : 'opacity-0')} />
                {all}
              </CommandItem>
              {opts.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); }}>
                  <Check className={cn('mr-2 h-3.5 w-3.5', value === o ? 'opacity-100' : 'opacity-0')} />
                  {o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
